// src/components/OrderEntry/OrderEntry.jsx
// -----------------------------------------------------------------------------
// ORDER ENTRY – FRONTEND LOOKUP TOOL (Refactored with Hook)
// -----------------------------------------------------------------------------
// This file now focuses ONLY on:
//   - Layout and UI composition
//   - All state + effects + handlers moved to useOrderEntryController hook
//
// UI is split into:
//   Area 1 -> EnterDetailsPanel
//   Area 2 -> HeaderFieldsPanel
//   Area 3 -> PartPanel
//   Area 4 -> LastPricePanel
//   Area 5 -> ContractPanel
//   Area 6 -> SalesRepsPanel
//   Dialog -> SpecialOrderDialog
// -----------------------------------------------------------------------------

import React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { getSugarAccountId, submitPickingSheet } from './api/orderEntryApi.js';
// Panels
import Badge6Actions from './components/Badge6Actions.jsx';
// Constants
import {
  COMPANY_OPTIONS,
  FUTURE_W,
  INPUT_HEIGHT,
  LIST_MIN_WIDTH,
  PART_FIELDS_WIDTH,
  ROW_HEIGHT,
} from './constants/orderEntryUi.js';
import SpecialOrderDialog from './dialogs/SpecialOrderDialog.jsx';
import useOrderEntryController from './hooks/useOrderEntryController.js';
import usePickingQueue from './hooks/usePickingQueue.js';
import ContractPanel from './panels/ContractPanel.jsx';
import EnterDetailsPanel from './panels/EnterDetailsPanel.jsx';
import HeaderFieldsPanel from './panels/HeaderFieldsPanel.jsx';
import LastPricePanel from './panels/LastPricePanel.jsx';
import PartPanel from './panels/PartPanel.jsx';
import PickingSheetsPanel from './panels/PickingSheetsPanel.jsx';
import SalesRepsPanel from './panels/SalesRepsPanel.jsx';
import { resolveSugarLookupShipToCode } from './utils/directShipRules.js';
import { isValidFreightAccountForCarrier } from './utils/freight.js';
import { formatMoneyInput, formatUSD, parseMoneyNumber, parseQtyInt } from './utils/parsers.js';
import normalizePostal5 from './utils/postal.js';

// ============================================================================
// BLOCK OE.0 — OrderEntry Component (thin render shell)
// ============================================================================

// ============================================================================
// BLOCK OE.BADGE6.COMPONENT.0 — Badge 6 actions (UI only)
// ----------------------------------------------------------------------------
// NOTE:
// - Render-only subcomponent extracted to:
//     src/components/OrderEntry/components/Badge6Actions.jsx
// - Keep Badge 6 as the ONLY location for copy/submit controls.
// ============================================================================

const normalizeShipToSearchInput = (value = '') =>
  String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9&'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Ampersands and apostrophes are literal searchable characters and count
// toward the same four-character minimum as letters and numbers.
const getShipToSearchRequiredLength = () => 4;

const isShipToSearchLongEnough = (value = '') => {
  const normalized = normalizeShipToSearchInput(value).replace(/\s+/g, '');
  return normalized.length >= getShipToSearchRequiredLength();
};

export default function OrderEntry({ userEmail, initialCompany = 'tzi' }) {
  // ===========================================================================
  // BLOCK OE.HOOK — All state, effects, handlers in useOrderEntryController
  // ===========================================================================
  const oe = useOrderEntryController({ userEmail, initialCompany });

  // ============================================================================
  // BLOCK OE.LOCALS.SUBMIT.0 — Extract submit deps to avoid `oe` in callbacks
  // (IMPORTANT: do NOT destructure setters from oe; ESLint can't assume stability.)
  // ============================================================================
  const {
    company,
    masId,
    shipId,
    poNumber,
    confirmTo,
    phone,
    setRevReq,
    clearShipToSearch,
    searchShipToAddresses,
  } = oe;

  // ===========================================================================
  // BLOCK OE.QUEUE — Picking sheets queue (floating squares)
  // ===========================================================================
  const pq = usePickingQueue();

  // ============================================================================
  /* BLOCK OE.CREDIT_HOLD.DERIVED.0 — UI-only derived flags */
  // ============================================================================
  const isCreditHold = Boolean(oe.creditHold);
  const hasSheets = Array.isArray(pq.sheets) && pq.sheets.length > 0;
  const hasSelectedAddress =
    oe.selectedAddressIndex !== null && oe.selectedAddressIndex !== undefined;
  const [shipToSearchInput, setShipToSearchInput] = React.useState('');
  const [shipToSearchValue, setShipToSearchValue] = React.useState(null);

  const shipToSearchOptions = React.useMemo(() => {
    const rows = Array.isArray(oe.shipToSearchRows) ? oe.shipToSearchRows : [];

    return rows
      .map((row, index) => {
        const shipToName = String(row?.shipToName || '').trim();
        if (!shipToName) {
          return null;
        }

        const customerNo = String(row?.customerNo || '').trim();
        const shipToCode = String(row?.shipToCode || '').trim();
        const shipToZip = normalizePostal5(
          row?.shipToZip || row?.shipToZipCode || row?.zipCode || ''
        );
        return {
          customerIndex: index,
          label: `${shipToName} - ${customerNo || '-----'} - ${shipToZip || '-----'}`,
          shipToName,
          customerNo,
          shipToCode,
          shipToZip,
          searchText: [shipToName, customerNo].filter(Boolean).join(' ').toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [oe.shipToSearchRows]);

  const selectedShipToSearchOption = React.useMemo(() => {
    if (!shipToSearchValue) {
      return null;
    }
    return shipToSearchValue;
  }, [shipToSearchValue]);

  React.useEffect(() => {
    setShipToSearchValue(null);
    setShipToSearchInput('');
  }, [company]);

  React.useEffect(() => {
    // A selected result is a label, not another query to add to the customer list.
    if (shipToSearchValue) {
      return undefined;
    }
    const normalized = normalizeShipToSearchInput(shipToSearchInput);

    if (!isShipToSearchLongEnough(normalized)) {
      clearShipToSearch();
      return undefined;
    }

    const timer = setTimeout(() => {
      searchShipToAddresses(normalized);
    }, 1000);

    return () => clearTimeout(timer);
  }, [clearShipToSearch, searchShipToAddresses, shipToSearchInput, shipToSearchValue]);

  // ============================================================================
  // BLOCK OE.TOTALS.0 — Running order total (sheets/panels only)
  // ============================================================================
  const orderTotalText = React.useMemo(() => {
    const sheets = Array.isArray(pq.sheets) ? pq.sheets : [];
    let total = 0;

    sheets.forEach((sheet) => {
      const lines = Array.isArray(sheet?.lines) ? sheet.lines : [];
      lines.forEach((line) => {
        const q = parseQtyInt(line?.qty);
        const p = parseMoneyNumber(line?.price);
        if (q && p) {
          total += q * p;
        }
      });
    });

    return `Order Total: ${formatUSD(total || 0)}`;
  }, [pq.sheets]);

  // Shared ref so PartPanel can forward tab to Input Sugar Note when Add Part is disabled
  const sugarButtonRef = React.useRef(null);

  // Shared refs for the phone-required tab flow
  const phoneRef = React.useRef(null);
  const addPartButtonRef = React.useRef(null);

  // ============================================================================
  // BLOCK OE.SUGAR.TAB.0 — Reusable named Sugar TAB handle (NOT a popup window)
  // ----------------------------------------------------------------------------
  // Behavior:
  // - First click opens a new tab named "sugarAccountTab"
  // - Subsequent clicks reuse that same tab and replace its URL
  // Limit:
  // - Works reliably only for tabs opened by this app (browser security)
  // ============================================================================
  const sugarTabRef = React.useRef(null);

  // ============================================================================
  // BLOCK OE.SUBMIT.0 — Submit / Copy handlers (Excel parity)
  // - Copy notes applies to ALL picking sheets
  // - Submit submits ALL sheets (normally 1)
  // - PO suffix rule: only append " (N)" when N > 1
  // ============================================================================
  const [submitBusyBySheetId, setSubmitBusyBySheetId] = React.useState({});

  // Sugar note "armed": user clicked Input Sugar Note (copied to clipboard)
  const [sugarNoteArmed, setSugarNoteArmed] = React.useState(false);

  // ============================================================================
  // BLOCK OE.SUBMIT.ERROR_DLG.0 — Center-screen submit error dialog (no red overlay)
  // ============================================================================
  const [submitErrorDlg, setSubmitErrorDlg] = React.useState({
    open: false,
    title: 'Submit Error',
    message: '',
    details: '',
    severity: 'error', // 'warning' | 'error'
  });

  const closeSubmitErrorDlg = React.useCallback(() => {
    setSubmitErrorDlg((s) => ({ ...s, open: false }));
  }, []);

  const openSubmitErrorDlg = React.useCallback((next) => {
    setSubmitErrorDlg({
      open: true,
      title: next?.title || 'Submit Error',
      message: next?.message || '',
      details: next?.details || '',
      severity: next?.severity || 'error',
    });
  }, []);

  // ============================================================================
  // BLOCK OE.TOAST.0 — Local toast (Excel-style guard messages)
  // ============================================================================
  const [toast, setToast] = React.useState({
    open: false,
    message: '',
    severity: 'info', // 'info' | 'success' | 'warning' | 'error'
  });

  const confirmMissing = !String(oe.confirmTo ?? '').trim();
  const phoneMissing = !String(oe.phone ?? '').trim();

  const showToast = React.useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const closeToast = React.useCallback(() => {
    setToast((t) => ({ ...t, open: false }));
  }, []);

  const handlePhoneTab = React.useCallback((e) => {
    if (e.key !== 'Tab' || e.shiftKey) {
      return;
    }

    if (addPartButtonRef.current && !addPartButtonRef.current.disabled) {
      e.preventDefault();
      addPartButtonRef.current.focus();
    }
  }, []);

  // ============================================================================
  // BLOCK OE.FRT_GUARD.DLG.0 — Add Part guard (FC requires valid UPS/FDX acct)
  // ============================================================================
  const [frtGuardOpen, setFrtGuardOpen] = React.useState(false);

  const closeFrtGuard = React.useCallback(() => {
    setFrtGuardOpen(false);
  }, []);

  const switchToPpaFromGuard = React.useCallback(() => {
    // flip to PPA + clear account (requested)
    if (typeof oe.handleFrtPayChange === 'function') {
      oe.handleFrtPayChange('PPA');
    } else if (typeof oe.setFrtPay === 'function') {
      oe.setFrtPay('PPA');
    }

    oe.setFrtAccount('');
    setFrtGuardOpen(false);
  }, [oe]);

  // ============================================================================
  // BLOCK OE.RESET.AFTER.SUBMIT.0 — Reset entry form after successful submit(s)
  // ============================================================================
  const resetEntryAfterSubmit = React.useCallback(() => {
    // 1) Clear customer context (MAS ID / ShipTo / LastPrice)
    //    - MUST be handled by controller because setters aren't exported individually.
    if (typeof oe.resetCustomerContext === 'function') {
      oe.resetCustomerContext();
    }

    // 2) Clear zip/location fields
    oe.setZip('');
    oe.setState('');
    oe.setCity('');

    // 3) Clear PartPanel entry fields
    oe.setPartNo('');
    oe.setPartInput('');
    oe.setQty('');
    oe.setUom('');
    oe.setPrice('');
    oe.setContractCode('');
    oe.setContractNo('');

    // 4) Clear order header fields
    oe.setPoNumber('');
    oe.setConfirmTo('Purchasing');
    oe.setPhone('');
    oe.setReceivingLine('');

    // 5) Reset badge gating
    setSugarNoteArmed(false);
    setRevReq(false);
  }, [oe, setRevReq]);

  // ============================================================================
  // BLOCK OE.CREDIT_HOLD.UI.0 — VBA parity on Credit Hold
  // ----------------------------------------------------------------------------
  // VBA:
  // - Hide Revision Required checkbox
  // - Show Customer-Credit Hold checkbox (checked)
  // - Hide Sugar link / disable Sugar note gate
  // - Popup message
  // ============================================================================
  const creditHoldAlertedRef = React.useRef(false);
  const [creditHoldDialogOpen, setCreditHoldDialogOpen] = React.useState(false);

  React.useEffect(() => {
    const isHold = Boolean(oe.creditHold);

    if (!isHold) {
      creditHoldAlertedRef.current = false;
      return;
    }

    // Credit-hold means: process WITHOUT Sugar note.
    setSugarNoteArmed(false);
    setRevReq(false);

    // VBA MsgBox (only once per hold activation)
    if (!creditHoldAlertedRef.current) {
      creditHoldAlertedRef.current = true;
      showToast(
        'Customer is on Credit Hold. Process Order without Sugar Note. ' +
          'Order will not ship until fixed in Sage.',
        'warning'
      );
    }
    // Show modal dialog when address is selected and customer is on credit hold
    if (isHold && hasSelectedAddress) {
      setCreditHoldDialogOpen(true);
    }
  }, [oe.creditHold, showToast, hasSelectedAddress, setRevReq]);

  React.useEffect(() => {
    if (!oe.creditHold) {
      setCreditHoldDialogOpen(false);
      return;
    }
    if (!hasSelectedAddress) {
      setCreditHoldDialogOpen(false);
    }
  }, [oe.creditHold, hasSelectedAddress]);

  // ============================================================================
  // OE.SUGAR.SPEC.0 — Per-line special order lines for Sugar note (DEDUPED)
  // ----------------------------------------------------------------------------
  // Dedupe rule (per sheet):
  // - Only suppress duplicates for the SAME partNo + specialOrderType + note
  //   (matches backend behavior that ties notes to a part)
  // ============================================================================
  const buildSpecialOrderLinesForSugar = React.useCallback((line, seenKeys) => {
    const partNo = String(line?.partNo || '').trim();
    const t = String(line?.specialOrderType || '')
      .trim()
      .toUpperCase();
    const note = String(line?.customerNote || '').trim();

    if (!partNo || !note) {
      return [];
    }

    // If caller provides a Set, dedupe within that scope (per sheet)
    if (seenKeys && typeof seenKeys.has === 'function') {
      const key = `${partNo}::${t}::${note}`;
      if (seenKeys.has(key)) {
        return [];
      }
      seenKeys.add(key);
    }

    return [note];
  }, []);

  // ============================================================================
  // BLOCK OE.PICKING.COPY_THIS_SHEET.0 — Copy Notes (This Sheet)
  // ----------------------------------------------------------------------------
  // Intent:
  // - PickingSheetsPanel button copies ONLY that sheet’s notes
  // - Does NOT open Sugar
  // - Does NOT arm/gate submit
  // ============================================================================
  const buildSugarNoteTextForSheet = React.useCallback(
    (sheet) => {
      const h = sheet?.header || {};
      const lines = Array.isArray(sheet?.lines) ? sheet.lines : [];
      const seenSpecialNotes = new Set();

      let text = `PO# ${poNumber || ''}\n`;
      text += `Confirm to: ${confirmTo || ''}\n`;
      text += `Phone: ${phone || ''}\n`;

      lines.forEach((l) => {
        let price = '';
        if (l.price != null && String(l.price).trim() !== '') {
          const p = String(l.price);
          price = p.startsWith('$') ? p : `$${p}`;
        }

        const contractChunk = h.contractCode ? ` (${h.contractCode}) ` : ' ';
        text += `Part NO: ${l.partNo} Qty:${l.qty} ${l.uom || ''} @${price}${contractChunk}${
          h.shipVia || ''
        }\n`;

        const extra = buildSpecialOrderLinesForSugar(l, seenSpecialNotes);
        extra.forEach((x) => {
          text += `${x}\n`;
        });
      });

      return text.trim();
    },
    [poNumber, confirmTo, phone, buildSpecialOrderLinesForSugar]
  );

  const handleCopyNotesThisSheet = React.useCallback(
    async (sheet) => {
      try {
        const noteText = buildSugarNoteTextForSheet(sheet);
        if (!noteText) {
          showToast('Nothing to copy for this sheet.', 'warning');
          return;
        }

        await navigator.clipboard.writeText(noteText);
        showToast('Copied notes for this sheet.', 'success');
      } catch (err) {
        showToast(`Copy failed: ${err?.message || 'Unknown error'}`, 'error');
      }
    },
    [buildSugarNoteTextForSheet, showToast]
  );

  // ============================================================================
  // BLOCK OE.PICKING.BUILD_ALL.0 — Build Sugar note text (ALL sheets) (Excel parity)
  // ----------------------------------------------------------------------------
  // Excel behavior:
  // - Notes include PO / Confirm-to / Phone header
  // - Then each line: Part, Qty, UOM, @Price, (Contract), ShipVia
  // - Append special-order customer note per line:
  //     - line.customerNote (special_order_items.Customer_Note)
  // ============================================================================
  const buildSugarNoteTextAll = React.useCallback(() => {
    const sheets = Array.isArray(pq.sheets) ? pq.sheets : [];
    const sorted = [...sheets].sort((a, b) => (a.pickSheetNo || 0) - (b.pickSheetNo || 0));

    let text =
      `PO# ${poNumber}\n` +
      `Confirm to: ${confirmTo || 'Purchasing'}\n` +
      `Phone: ${phone || ''}\n`;

    sorted.forEach((sheet, idx) => {
      const h = sheet.header || {};
      const lines = Array.isArray(sheet.lines) ? sheet.lines : [];
      const seenSpecialNotes = new Set();
      const pickNo = Number(sheet.pickSheetNo) || 1;

      // If multiple sheets exist, add a per-sheet header (PO suffix only when N > 1)
      if (sorted.length > 1) {
        const poForSheet = pickNo > 1 ? `${poNumber} (${pickNo})` : poNumber;
        text += `\nPO# ${poForSheet}\n`;
      } else if (idx === 0) {
        // single-sheet default behavior already has header above
      }

      lines.forEach((l) => {
        let price = '';
        if (l.price != null && String(l.price).trim() !== '') {
          const p = String(l.price);
          price = p.startsWith('$') ? p : `$${p}`;
        }

        const contractChunk = h.contractCode ? ` (${h.contractCode}) ` : ' ';
        text += `Part NO: ${l.partNo} Qty:${l.qty} ${l.uom || ''} @${price}${contractChunk}${
          h.shipVia || ''
        }\n`;

        // Include only Customer_Note text for special-order lines
        const extra = buildSpecialOrderLinesForSugar(l, seenSpecialNotes);
        extra.forEach((x) => {
          text += `${x}\n`;
        });
      });
    });

    return text.trim();
  }, [pq.sheets, poNumber, confirmTo, phone, buildSpecialOrderLinesForSugar]);

  // ============================================================================
  // BLOCK OE.PQ.COHERENCE.0 — Prevent mixed-customer picking sheets
  // Rule: picking sheets must match current masId/shipId
  // ============================================================================
  const lastKeyRef = React.useRef('');

  React.useEffect(() => {
    const key = `${oe.company || ''}::${oe.masId || ''}::${oe.shipId || ''}`;

    // First render: initialize without clearing
    if (!lastKeyRef.current) {
      lastKeyRef.current = key;
      return;
    }

    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;

      // If there are existing sheets, clear them to prevent mixing
      if (Array.isArray(pq.sheets) && pq.sheets.length > 0) {
        pq.resetForCustomer?.();
      }
    }
  }, [oe.company, oe.masId, oe.shipId, pq]);

  // ============================================================================
  // BLOCK OE.BADGE6.INPUT_SUGAR.0 — handleInputSugarNote()
  // ----------------------------------------------------------------------------
  // Intent: Excel parity "LblSugarLink_Click":
  // - Build STANDARD Sugar note for ALL picking sheets/lines
  // - Copy to clipboard
  // - Lookup customer_contracts.id_c (customerNo + shipToCode)
  // - Open Sugar URL in a POPUP WINDOW (must be synchronous for Chrome popup)
  // - Then arm submit so Submit appears in Badge 6
  //
  // NOTE: window.open() MUST be called SYNCHRONOUSLY on user click with NO
  // async/await before it. This allows Chrome to recognize it as user-initiated
  // and show a popup block notification instead of silently opening a tab.
  // ============================================================================
  const handleInputSugarNote = React.useCallback(() => {
    // Early validation (before any async operations)
    if (!masId || !shipId) {
      showToast('Select a customer and ship-to first.', 'warning');
      return;
    }
    if (!hasSheets) {
      showToast('No picking sheets to build a Sugar note from.', 'warning');
      // setSugarNoteArmed(false);    //disables sugarnote button if no part
      // return;
    }

    setSugarNoteArmed(true);

    // ============================================================================
    // BLOCK OE.SUGAR.TAB.OPEN.0 — Open/reuse a NAMED TAB (no popup features)
    // ----------------------------------------------------------------------------
    // Requirements:
    // - Must be synchronous (no await before) to avoid browser blocking
    // - Named tab allows reuse across clicks
    // ----------------------------------------------------------------------------
    // NOTE:
    // - We intentionally do NOT use "noopener" here because we need a window handle
    //   to reuse and navigate the same tab on subsequent clicks.
    // ============================================================================
    const tabName = 'sugarAccountTab';
    const tabWin = window.open('about:blank', tabName);
    sugarTabRef.current = tabWin;

    if (tabWin && !tabWin.closed) {
      // Optional friendly loading view (best-effort; may be ignored cross-origin later)
      try {
        tabWin.document.title = 'Opening Sugar…';
        tabWin.document.body.innerHTML = `
          <div style="
            font-family: system-ui, sans-serif;
            padding: 24px;
            font-size: 16px;
          ">
            Opening Sugar account…
          </div>
        `;
      } catch (e) {
        // ignore
      }
    }

    // NOW do async operations (popup is already open and Chrome knows it's user-initiated)
    (async () => {
      try {
        // Build the STANDARD note for ALL sheets (Excel parity)
        const noteText = buildSugarNoteTextAll();

        // Copy to clipboard (VBA Copy2CB parity)
        await navigator.clipboard.writeText(noteText);

        // Lookup Sugar account id_c (VBA customer_contracts query parity)
        const sugarLookupShipToCode = resolveSugarLookupShipToCode(
          company,
          shipId,
          oe.selectedAddressShipId
        );

        const res = await getSugarAccountId({
          company,
          customerNo: masId,
          shipToCode: sugarLookupShipToCode,
        });

        if (res?.warning) {
          showToast(res.warning, 'warning');
        }

        if (!res?.id_c) {
          showToast('COULD NOT FIND CUSTOMER ACCOUNT IN SUGAR', 'warning');
          const fallbackUrl = 'https://sugar.tzmedical.com/#Accounts';
          const win = sugarTabRef.current;
          if (win && !win.closed) {
            win.location.href = fallbackUrl;
            win.focus();
          }
          return;
        }

        const sugarUrl = `https://sugar.tzmedical.com/#Accounts/${res.id_c}`;

        // Navigate the named tab that was already opened
        const win = sugarTabRef.current;
        if (win && !win.closed) {
          win.location.href = sugarUrl;
          win.focus();
        }

        // Arm submit AFTER copy + successful id_c lookup
        setSugarNoteArmed(true);
        setRevReq(false);

        showToast('Sugar note copied. Sugar opened in a new window.', 'success');
      } catch (err) {
        showToast(`Sugar note action failed: ${err?.message || 'Unknown error'}`, 'error');
        setSugarNoteArmed(false);
      }
    })();
  }, [
    company,
    masId,
    shipId,
    hasSheets,
    buildSugarNoteTextAll,
    showToast,
    setRevReq,
    oe.selectedAddressShipId,
  ]);

  const handleOpenDirectShipPatient = oe.openDirectShipPatientDialog;
  const handleCloseDirectShipPatient = oe.closeDirectShipPatientDialog;
  const handleSubmitDirectShipPatient = oe.submitDirectShipPatient;

  // ===========================================================================
  // BLOCK OE.SUBMIT.ONE.0 — Submit one picking sheet (Final parity)
  // ----------------------------------------------------------------------------
  // Frontend responsibilities:
  // - Send ordered repSlots (from SalesRepsPanel/controller)
  // - Send contractCode from THIS sheet header (per picking sheet)
  // - Send flags and header fields
  //
  // Backend responsibilities:
  // - Apply intl/house/split overrides
  // - Pack reps into rep1..rep3 when NO override
  // - Append contractCode LAST into next available rep slot
  // ===========================================================================
  const submitOneSheet = React.useCallback(
    async (sheet) => {
      const sheetId = sheet?.sheetId;
      const lines = sheet?.lines;
      const label = sheet?.label || `Sheet #${sheet?.pickSheetNo || 1}`;
      const header = sheet?.header || {};

      const cleanLines = Array.isArray(lines) ? lines : [];
      if (cleanLines.length === 0) {
        throw new Error('No lines to submit.');
      }

      // ---------------------------------------------------------------------------
      // BLOCK OE.SUBMIT.DIRECT_SHIP.0 — TMS patient destination gating
      // ---------------------------------------------------------------------------
      if (oe.directShipPatientActive && !oe.directShipPatientComplete) {
        oe.openDirectShipPatientDialog?.();
        showToast('Complete the Direct Ship Patient address before final submit.', 'warning');
        return { ok: false, code: 'DIRECT_SHIP_PATIENT_REQUIRED' };
      }

      const directShipPatient = oe.directShipPatientActive
        ? {
            shipToName: String(oe.directShipPatient?.shipToName || '').trim(),
            address1: String(oe.directShipPatient?.address1 || '').trim(),
            address2: String(oe.directShipPatient?.address2 || '').trim(),
            city: String(oe.directShipPatient?.city || '').trim(),
            state: String(oe.directShipPatient?.state || '').trim(),
            zip: String(oe.directShipPatient?.zip || '').trim(),
          }
        : null;

      // ---------------------------------------------------------------------------
      // BLOCK OE.SUBMIT.SHEET_HEADER.0 — Per-sheet submit field source
      // ----------------------------------------------------------------------------
      // Rule:
      // - Submit shipping/freight/terms/contract from THIS sheet header snapshot.
      // - Fallback to current entry values only when older sheet data is missing.
      // ---------------------------------------------------------------------------
      const lineContractCode =
        cleanLines.map((r) => String(r?.contractCode || '').trim()).find(Boolean) || '';

      const headerShipVia = String(header?.shipVia ?? '').trim();
      const headerFrtPay = String(header?.freightMethod ?? header?.frtPay ?? '').trim();
      const headerFrtAccount = String(header?.freightContract ?? header?.frtAccount ?? '').trim();
      const headerTermsCode = String(header?.termsCode ?? '').trim();
      const headerContractCode = String(header?.contractCode ?? '').trim();

      const fallbackShipVia = String(oe.shipVia ?? '').trim();
      const fallbackFrtPay = String(oe.frtPay ?? '').trim();
      const fallbackFrtAccount = String(oe.frtAccount ?? '').trim();
      const fallbackTermsCode = String(oe.termsValue ?? '').trim();

      const sheetShipVia = headerShipVia || fallbackShipVia;
      const sheetFrtPay = headerFrtPay || fallbackFrtPay;
      const sheetFrtAccount = headerFrtAccount || fallbackFrtAccount;
      const sheetTermsCode = headerTermsCode || fallbackTermsCode;
      const sheetContractCode = headerContractCode || lineContractCode;

      // ---------------------------------------------------------------------------
      // BLOCK OE.SUBMIT.FRT_GATING.1 — Sheet-level FC gating for shipaccount
      // ----------------------------------------------------------------------------
      // Rule:
      // - Include freight account only when this sheet is Collect (FC).
      // - Prefer explicit sheet freight method; fallback to shipVia token if missing.
      // ---------------------------------------------------------------------------
      const frtPayUpper = String(sheetFrtPay || '').toUpperCase();
      const shipViaUpper = String(sheetShipVia || '').toUpperCase();

      const fcByPay = frtPayUpper === 'FC';
      const fcByShipVia = /(^|[\s-])FC($|[\s)])/i.test(shipViaUpper);
      const effectiveFc = fcByPay || (!sheetFrtPay && fcByShipVia);

      const submitFrtPay = sheetFrtPay || (fcByShipVia ? 'FC' : '');
      const submitFrtAccount = effectiveFc ? sheetFrtAccount : '';

      const payload = {
        company: oe.company,

        customerNo: oe.masId,
        shipToCode: oe.shipId,

        customerName: directShipPatient?.shipToName || oe.customerName || '',
        shipAddress1: directShipPatient?.address1 || oe.receivingLine || oe.shipAddress1 || '',
        shipAddress2: directShipPatient?.address2 || oe.shipAddress2 || '',
        city: directShipPatient?.city || oe.city || '',
        state: directShipPatient?.state || oe.state || '',
        zip: directShipPatient?.zip || oe.zip || '',

        // Explicit nested override lets the backend enforce TMS + ShipTo P safely.
        directShipPatient,

        // Flags that influence rep logic (backend)
        splitrep: oe.splitCommission ? 'Y' : 'N',
        splitCommission: Boolean(oe.splitCommission),
        houseAccount: Boolean(oe.houseAccount),
        omitCommentCodes: Boolean(oe.omitCommentCodes),
        customCommentsText: String(oe.customCommentsText || '').trim(),
        internationalSale: Boolean(oe.internationalSale),

        // ---------------------------------------------------------------------------
        // BLOCK OE.SUBMIT.REPS.0 — Submit rep selection with House Account override
        // ----------------------------------------------------------------------------
        // If customer is in house_accounts, UI shows oe.houseAccountReps (rep1/rep2).
        // We must send those to backend so submit matches UI.
        // ---------------------------------------------------------------------------
        houseAccountReps: oe.houseAccountReps || null,

        repSlots: (() => {
          const ha1 = String(oe.houseAccountReps?.rep1 ?? '').trim();
          const ha2 = String(oe.houseAccountReps?.rep2 ?? '').trim();

          // If house override exists, submit exactly those reps (max 2), ignore search reps.
          if (ha1) {
            const out = [{ div: '00', rep: ha1 }];
            if (ha2) {
              out.push({ div: '00', rep: ha2 });
            }
            return out;
          }

          // Otherwise use controller-provided rep ordering
          return Array.isArray(oe.repSlots) ? oe.repSlots : [];
        })(),

        poNumber: oe.poNumber || '',
        confirmTo: oe.confirmTo || '',
        phone: oe.phone || '',

        shipVia: sheetShipVia,
        termsCode: sheetTermsCode,
        frtPay: submitFrtPay,
        frtAccount: submitFrtAccount,

        // Per-picking-sheet contractCode (must ALWAYS be passed)
        contractCode: sheetContractCode,

        // BLOCK OE.SUBMIT.SPLIT_PO.0 — tell backend how many pick sheets exist
        totalPickSheets: Array.isArray(pq.sheets) ? pq.sheets.length : 0,

        pickSheetNo: sheet?.pickSheetNo || 1,
        user: oe.user || '',

        // Special order flags are per-sheet (derived from line content)
        flags: {
          specOrderBo: cleanLines.some(
            (r) => String(r?.specialOrderType || '').toUpperCase() === 'BO'
          ),
          specOrderMo: cleanLines.some(
            (r) => String(r?.specialOrderType || '').toUpperCase() === 'MO'
          ),
          specOrderDs: cleanLines.some(
            (r) => String(r?.specialOrderType || '').toUpperCase() === 'DS'
          ),
          specOrderBl: cleanLines.some(
            (r) => String(r?.specialOrderType || '').toUpperCase() === 'BL'
          ),

          creditHold: Boolean(oe.creditHold),
          revReq: Boolean(oe.revReq),
          zeroInsurance: Boolean(oe.zeroInsurance),
        },

        lines: cleanLines.map((r) => ({
          partNo: String(r?.partNo || '').trim(),
          qty: String(r?.qty || '').trim(),

          // NEW — special order metadata used by submit-order.js to insert /C rows
          specialOrderType: String(r?.specialOrderType || '').trim(),
          customerNote: oe.omitCommentCodes ? '' : String(r?.customerNote || '').trim(),

          uom: String(r?.uom || '').trim(),
          price: String(r?.price || '').trim(),
        })),
      };

      setSubmitBusyBySheetId((prev) => ({ ...prev, [sheetId]: true }));

      try {
        await submitPickingSheet(payload);
        showToast(`${label} submitted successfully.`, 'success');

        // Clear ONLY the submitted sheet
        pq.removeSheet(sheetId);

        // IMPORTANT:
        // Do NOT reset the whole entry form here, because Badge 6 may be
        // submitting multiple sheets sequentially. Reset happens once after
        // all sheets succeed (see handleSubmitActiveSheet).
        return { ok: true };
      } catch (err) {
        // ---------------------------------------------------------------
        // BLOCK OE.SUBMIT.ERR_PARSE.0 — Robust status/message extraction
        // (handles cases where err.response is missing but message contains status)
        // ---------------------------------------------------------------
        const statusFromResponse = err?.response?.status;
        const msgFromResponse =
          err?.response?.data?.message ||
          err?.response?.data?.detail?.message ||
          err?.response?.data?.error ||
          '';

        const fallbackMsg = err?.message || String(err || '');

        // Try to parse "...status code 409" from axios-style message strings
        const parsedStatus = (() => {
          const m = String(fallbackMsg).match(/\bstatus code\s+(\d{3})\b/i);
          return m ? Number(m[1]) : undefined;
        })();

        const status = statusFromResponse || err?.status || parsedStatus || 0;
        const serverMsg = msgFromResponse || '';

        // ---------------------------------------------------------------
        // BLOCK OE.SUBMIT.DUP_PO.1 — Friendly duplicate PO handling (409)
        // ---------------------------------------------------------------
        if (status === 409) {
          // Optional: make the sheet label prettier (remove "Sheet #1:" prefix)
          const po = String(oe.poNumber || '').trim();
          const cleanLabel = String(label || '')
            .replace(/^Sheet\s+#\d+:\s*/i, '')
            .trim();

          openSubmitErrorDlg({
            title: 'Duplicate PO (Already Submitted)',
            severity: 'warning',
            message:
              `This order looks like it was already submitted.\n\n${
                po ? `PO#: ${po}\n` : ''
              }What to do next:\n` +
              `• Check Sage for an existing/open order for this PO\n` +
              `• If you need a change, use Revision Requested (or update the existing order)\n`,
            // If your backend sends a useful explanation, show it; otherwise keep it clean
            details: serverMsg || cleanLabel || 'Legacy insert returned HTTP 409 (conflict).',
          });

          return { ok: false, status: 409, code: 'DUPLICATE_PO' };
        }

        // ---------------------------------------------------------------
        // BLOCK OE.SUBMIT.GENERIC_FAIL.1 — Friendly generic failure
        // ---------------------------------------------------------------
        const msg = serverMsg || fallbackMsg || 'Submit failed';

        openSubmitErrorDlg({
          title: 'Submit Failed',
          severity: 'error',
          message: `${label}: ${msg}`,
          details: status ? `HTTP ${status}` : '',
        });

        return { ok: false, status: status || 0, code: 'SUBMIT_FAILED' };
      } finally {
        setSubmitBusyBySheetId((prev) => ({ ...prev, [sheetId]: false }));
      }
    },
    [oe, pq, showToast, openSubmitErrorDlg]
  );

  // ============================================================================
  // Badge 6: "Submit" (submits ALL sheets — usually just 1)
  const handleSubmitActiveSheet = React.useCallback(async () => {
    const sheets = Array.isArray(pq.sheets) ? pq.sheets : [];
    if (sheets.length === 0) {
      showToast('No picking sheets to submit.', 'warning');
      return;
    }

    const sorted = [...sheets].sort((a, b) => (a.pickSheetNo || 0) - (b.pickSheetNo || 0));

    // Submit sequentially so you get clean errors and no mixed busy state
    for (let i = 0; i < sorted.length; i += 1) {
      const res = await submitOneSheet(sorted[i]);
      if (!res?.ok) {
        // Stop submitting additional sheets after a failure
        return;
      }
    }

    // Reset full form after successful submit(s)
    resetEntryAfterSubmit();
  }, [pq.sheets, showToast, submitOneSheet, resetEntryAfterSubmit]);

  // ============================================================================
  // BLOCK OE.REMOVE.LINE.0 — Remove a line from a sheet (restore to entry + relookup)
  // ----------------------------------------------------------------------------
  // Behavior:
  // - Capture the line being removed
  // - Remove it from the picking queue
  // - Restore PartPanel fields (partNo, qty, uom, price)
  // - Restore contractCode from sheet header if present
  // - Trigger last-order + contract lookups via oe.handlePartLookup()
  // ============================================================================
  const handleRemoveLine = React.useCallback(
    (sheetId, lineId) => {
      const sheets = Array.isArray(pq.sheets) ? pq.sheets : [];
      const sheet = sheets.find((s) => s.sheetId === sheetId);
      const lines = Array.isArray(sheet?.lines) ? sheet.lines : [];
      const line = lines.find((l) => l.lineId === lineId);

      // 1) Remove from queue
      pq.removeLine(sheetId, lineId);

      // 2) Restore entry fields
      if (!line) {
        return;
      }

      const partNo = String(line.partNo ?? '');
      const qty = String(line.qty ?? '');
      const uom = String(line.uom ?? '');
      const price = line.price != null ? String(line.price) : '';

      oe.setPartNo(partNo);
      oe.setPartInput(partNo);
      oe.setQty(qty);
      oe.setUom(uom);

      // � restore picking-sheet price first
      oe.setPrice(price);

      // 3) Refresh panels WITHOUT overwriting price
      oe.handlePartLookup(partNo, { preserveEntryPrice: true });
    },
    [pq, oe]
  );

  const handlePoEnter = oe.focusPartNo;
  const lookupPart = oe.handlePartLookup;
  const carrierChange = oe.handleCarrierChange;
  const freightPayChange = oe.handleFrtPayChange;
  const handleFrtAccountUserEdit = oe.onFrtAccountUserEdit;
  const handleZeroInsuranceManual = oe.onZeroInsuranceManual;
  const handleRemoveSheet = pq.removeSheet;
  const handleClearAllSheets = pq.clearAll;
  const handleCloseCreditHoldDialog = () => setCreditHoldDialogOpen(false);
  const handleRetryFreightAccounts = () => {
    if (typeof oe.retryFreightAccounts === 'function') {
      oe.retryFreightAccounts();
    }
  };
  const handleClearForm = () => {
    setShipToSearchValue(null);
    setShipToSearchInput('');
    if (typeof oe.clearShipToSearch === 'function') {
      oe.clearShipToSearch();
    }
    if (typeof oe.resetAll === 'function') {
      oe.resetAll();
    }
  };
  const handleCloseFreightSaveDialog = oe.closeFreightSaveDialog;
  const handleSubmitFreightSave = oe.submitFreightSave;
  const handleCloseFreightRemoveDialog = oe.closeFreightRemoveDialog;
  const handleSubmitFreightRemove = oe.submitFreightRemove;
  const handleCloseSpecialDialog = oe.closeSpecialDialog;
  const handleCloseSubmitErrorDialog = closeSubmitErrorDlg;
  const handleCloseToast = closeToast;

  // ===========================================================================
  // BLOCK OE.20 — RENDER — RENDER
  // ===========================================================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card variant="outlined">
        {/* Credit Hold dialog shown when address selected and customer flagged */}
        <Dialog open={creditHoldDialogOpen} onClose={handleCloseCreditHoldDialog}>
          <DialogTitle>Customer on Credit Hold</DialogTitle>
          <DialogContent>
            <Typography>
              This customer is on credit hold. The order will not ship until the hold is resolved.
              Input Sugar Note is disabled and the Credit Hold checkbox is shown.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreditHoldDialog}>OK</Button>
          </DialogActions>
        </Dialog>
        <CardHeader
          title="Enter Details"
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 320, width: 320 }}
                options={shipToSearchOptions}
                value={selectedShipToSearchOption}
                inputValue={shipToSearchInput}
                loading={oe.shipToSearchLoading}
                filterOptions={(options, state) => {
                  const queryTokens = normalizeShipToSearchInput(state.inputValue)
                    .split(/\s+/)
                    .map((token) => token.trim())
                    .filter(Boolean);

                  if (!queryTokens.length) {
                    return options;
                  }

                  return options.filter((option) => {
                    const haystack = String(option?.searchText || '')
                      .toUpperCase()
                      .replace(/[^A-Z0-9&']/g, ' ')
                      .replace(/\s+/g, ' ')
                      .trim();

                    let fromIndex = 0;
                    for (const token of queryTokens) {
                      const idx = haystack.indexOf(token, fromIndex);
                      if (idx < 0) {
                        return false;
                      }
                      fromIndex = idx + token.length;
                    }

                    return true;
                  });
                }}
                onInputChange={(_, newInputValue, reason) => {
                  // MUI emits reset/select/blur callbacks for programmatic changes.
                  // Only user typing or clearing may change the search text here.
                  if (reason !== 'input' && reason !== 'clear') {
                    return;
                  }
                  setShipToSearchValue(null);
                  setShipToSearchInput(String(newInputValue || '').toUpperCase());
                }}
                onChange={(_, nextOption) => {
                  if (!nextOption) {
                    setShipToSearchValue(null);
                    setShipToSearchInput('');
                    oe.clearShipToSearch();
                    return;
                  }

                  setShipToSearchValue(nextOption);
                  setShipToSearchInput(nextOption.label);
                  oe.handleShipToSearchSelect(nextOption);
                }}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.customerNo === value?.customerNo &&
                  option?.shipToName === value?.shipToName
                }
                clearOnEscape
                noOptionsText={
                  oe.shipToSearchError ||
                  (!isShipToSearchLongEnough(shipToSearchInput)
                    ? `Type at least ${getShipToSearchRequiredLength()} characters to search`
                    : 'No ship-to addresses found')
                }
                renderInput={(params) => <TextField {...params} label="Ship-to Search" />}
              />

              <Button variant="contained" color="primary" size="small" onClick={handleClearForm}>
                Clear Form
              </Button>

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="company-label">Company</InputLabel>
                <Select
                  labelId="company-label"
                  label="Company"
                  value={oe.company}
                  onChange={oe.handleCompanyChange}
                  disabled={oe.disabled}
                >
                  {COMPANY_OPTIONS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          }
        />
        <Divider />

        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                alignItems: 'start',
                justifyItems: 'start',
                gridTemplateAreas: {
                  xs: `
                    "one"
                    "two"
                    "part"
                    "price"
                    "contract"
                    "reps"
                  `,
                  md: `
                    "one     one"
                    "two     two"
                    "part    price"
                    "part    contract"
                    "part    reps"
                  `,
                },
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'max-content minmax(0, 1fr)',
                },
                overflowX: 'visible',
                gridAutoRows: 'max-content',
              }}
            >
              {/* =========================
                  AREA 1 - Customer Selection
                 ========================= */}
              <Box sx={{ gridArea: 'one' }}>
                <EnterDetailsPanel
                  disabled={oe.disabled}
                  zip={oe.zip}
                  setZip={oe.setZip}
                  city={oe.city}
                  state={oe.state}
                  customers={oe.customers}
                  selectedCustomer={oe.selectedCustomer}
                  onCustomerSelect={oe.handleCustomerSelect}
                  customerListFocusNonce={oe.customerListFocusNonce}
                  addresses={oe.addresses}
                  selectedAddressIndex={oe.selectedAddressIndex}
                  onAddressClick={oe.handleAddressClick}
                  billTo={oe.billTo}
                  notes={oe.notes}
                  directShipPatientActive={oe.directShipPatientActive}
                  directShipPatientComplete={oe.directShipPatientComplete}
                  directShipPatient={oe.directShipPatient}
                  directShipDialogOpen={oe.directShipDialogOpen}
                  directShipStudies={oe.directShipStudies}
                  directShipStudiesLoading={oe.directShipStudiesLoading}
                  directShipStudiesError={oe.directShipStudiesError}
                  onOpenDirectShipPatient={handleOpenDirectShipPatient}
                  onCloseDirectShipPatient={handleCloseDirectShipPatient}
                  onSubmitDirectShipPatient={handleSubmitDirectShipPatient}
                  rowHeight={ROW_HEIGHT}
                  listMinWidth={LIST_MIN_WIDTH}
                  zipRef={oe.zipRef}
                  focusPoNumber={oe.focusPoNumber}
                />
              </Box>

              {/* =========================
                  AREA 2 - Order Header Fields
                 ========================= */}
              <Box sx={{ gridArea: 'two' }}>
                <HeaderFieldsPanel
                  disabled={oe.disabled}
                  company={oe.company}
                  masId={oe.masId}
                  shipId={oe.shipId}
                  selectedAddressShipId={oe.selectedAddressShipId}
                  onShipIdChange={oe.handleShipIdChange}
                  poNumber={oe.poNumber}
                  setPoNumber={oe.setPoNumber}
                  onPoBlur={oe.handlePoBlur}
                  confirmTo={oe.confirmTo}
                  setConfirmTo={oe.setConfirmTo}
                  confirmToOptions={oe.confirmToOptions}
                  confirmToLoading={oe.confirmToLoading}
                  phone={oe.phone}
                  setPhone={oe.setPhone}
                  phoneRef={phoneRef}
                  onPhoneTab={handlePhoneTab}
                  receivingLine={oe.receivingLine}
                  setReceivingLine={oe.setReceivingLine}
                  confirmRequired={confirmMissing}
                  phoneRequired={phoneMissing}
                  poRef={oe.poRef}
                  onPoEnter={handlePoEnter}
                />
              </Box>

              {/* =========================
                  AREA 3 - Part & Shipping Details
                 ========================= */}
              <Box sx={{ gridArea: 'part' }}>
                <PartPanel
                  company={oe.company}
                  disabled={oe.badge3Disabled}
                  directShipPatientActive={oe.directShipPatientActive}
                  partOptions={oe.partOptions}
                  partLoading={oe.partLoading}
                  partNo={oe.partNo}
                  setPartNo={oe.setPartNo}
                  partInput={oe.partInput}
                  setPartInput={oe.setPartInput}
                  handlePartLookup={lookupPart}
                  findClosestPartOption={oe.findClosestPartOption}
                  qty={oe.qty}
                  setQty={oe.setQty}
                  parseQtyInt={parseQtyInt}
                  uom={oe.uom}
                  setUom={oe.setUom}
                  price={oe.price}
                  setPrice={oe.setPrice}
                  formatMoneyInput={formatMoneyInput}
                  orderTotalText={orderTotalText}
                  productQuote={oe.productQuote}
                  productQuoteDialogOpen={oe.productQuoteDialogOpen}
                  productQuoteDefaultUsePrice={oe.productQuoteDefaultUsePrice}
                  onAcceptProductQuote={oe.handleAcceptProductQuote}
                  onDeclineProductQuote={oe.handleDeclineProductQuote}
                  shipVia={oe.shipVia}
                  setShipVia={oe.setShipVia}
                  shipViaList={oe.shipViaList}
                  carrier={oe.carrier}
                  setCarrier={carrierChange}
                  frtPay={oe.frtPay}
                  setFrtPay={freightPayChange}
                  shipEtaMap={oe.shipEtaMap}
                  frtAccount={oe.frtAccount}
                  setFrtAccount={oe.setFrtAccount}
                  onFrtAccountUserEdit={handleFrtAccountUserEdit}
                  onFrtAccountBlur={oe.handleFrtAccountBlur}
                  contractCode={oe.contractCode}
                  setContractCode={oe.setContractCode}
                  contractNo={oe.contractNo}
                  setContractNo={oe.setContractNo}
                  splitCommission={oe.splitCommission}
                  setSplitCommission={oe.setSplitCommission}
                  specOrderBo={oe.specOrderBo}
                  setSpecOrderBo={oe.setSpecOrderBo}
                  specOrderMo={oe.specOrderMo}
                  setSpecOrderMo={oe.setSpecOrderMo}
                  specOrderDs={oe.specOrderDs}
                  setSpecOrderDs={oe.setSpecOrderDs}
                  specOrderBl={oe.specOrderBl}
                  setSpecOrderBl={oe.setSpecOrderBl}
                  houseAccount={oe.houseAccount}
                  setHouseAccount={oe.setHouseAccount}
                  omitCommentCodes={oe.omitCommentCodes}
                  setOmitCommentCodes={oe.setOmitCommentCodes}
                  customCommentsText={oe.customCommentsText}
                  setCustomCommentsText={oe.setCustomCommentsText}
                  internationalSale={oe.internationalSale}
                  setInternationalSale={oe.setInternationalSale}
                  zeroInsurance={oe.zeroInsurance}
                  setZeroInsurance={oe.setZeroInsurance}
                  inputHeight={INPUT_HEIGHT}
                  partFieldsWidth={PART_FIELDS_WIDTH}
                  futureWidth={FUTURE_W}
                  partRef={oe.partRef}
                  qtyRef={oe.qtyRef}
                  phoneRef={phoneRef}
                  addPartButtonRef={addPartButtonRef}
                  termsValue={oe.termsValue}
                  setTermsValue={oe.setTermsValue}
                  termsOptions={oe.termsOptions}
                  onZeroInsuranceManual={handleZeroInsuranceManual}
                  sugarButtonRef={sugarButtonRef}
                  confirmTo={oe.confirmTo}
                  phone={oe.phone}
                  onAddPart={() => {
                    // ========================================================================
                    // BLOCK OE.ADD_PART.GUARD.0 — FC requires valid UPS/FDX account
                    // ========================================================================
                    {
                      const carrierUpper = String(oe.carrier ?? '')
                        .trim()
                        .toUpperCase();
                      const frtPayUpper = String(oe.frtPay ?? '')
                        .trim()
                        .toUpperCase();
                      const strict =
                        frtPayUpper === 'FC' && (carrierUpper === 'UPS' || carrierUpper === 'FDX');

                      if (strict) {
                        const ok = isValidFreightAccountForCarrier({
                          carrier: carrierUpper,
                          accountNo: oe.frtAccount,
                        });

                        if (!ok) {
                          setFrtGuardOpen(true);
                          return; // BLOCK add
                        }
                      }
                    }
                    // ---------------------------------------------------------------------------
                    // BLOCK OE.SPEC.DERIVE.0 — Derive special order type (no nested ternary)
                    // Priority is explicit and deterministic (first true wins).
                    // ---------------------------------------------------------------------------
                    let derivedSpecialType = '';
                    if (oe.specOrderBo) {
                      derivedSpecialType = 'BO';
                    } else if (oe.specOrderMo) {
                      derivedSpecialType = 'MO';
                    } else if (oe.specOrderDs) {
                      derivedSpecialType = 'DS';
                    } else if (oe.specOrderBl) {
                      derivedSpecialType = 'BL';
                    }

                    const entryForQueue = {
                      ...oe,
                      contractNo: '',

                      // Carry per-line special order metadata into the picking sheet line
                      // (used for Sugar note + submit-order comment rows)
                      specialOrderType: derivedSpecialType,
                      customerNote: String(oe.specOrderCustomerNote || '').trim(),
                    };

                    pq.addPartFromEntry(entryForQueue);

                    // 2) clear entry fields per your rule set
                    //    (ShipVia / Terms / freight selections stay as-is)
                    oe.setPartNo('');
                    oe.setPartInput('');
                    oe.setQty('');
                    oe.setUom('');
                    oe.setPrice('');

                    // Contract textbox cleared (contractRows/lastPrice display will naturally change on next lookup)
                    oe.setContractCode('');
                    oe.setContractNo('');

                    // Return focus to part number after adding
                    if (typeof oe.focusPartNo === 'function') {
                      oe.focusPartNo();
                    }
                  }}
                />
              </Box>

              {/* =========================
                  AREA 4 - Last Price Info
                 ========================= */}
              <Box sx={{ gridArea: 'price' }}>
                <LastPricePanel
                  masId={oe.masId}
                  shipId={oe.shipId}
                  lastPriceRow={oe.lastPriceRow}
                  rowHeight={ROW_HEIGHT}
                />
              </Box>

              {/* =========================
                  AREA 5 - Contract Info
                 ========================= */}
              <Box sx={{ gridArea: 'contract' }}>
                <ContractPanel
                  masId={oe.masId}
                  shipId={oe.shipId}
                  contractRows={oe.contractRows}
                  rowHeight={ROW_HEIGHT}
                />
              </Box>

              {/* =========================
                  AREA 6 - Sales Reps + Submit Controls (Excel parity)
                 ========================= */}
              <Box sx={{ gridArea: 'reps' }}>
                <Stack spacing={1.25}>
                  {/* Badge 6 top actions */}
                  <Badge6Actions
                    hasSheets={hasSheets}
                    disabled={oe.disabled}
                    showCreditHold={isCreditHold}
                    creditHoldChecked={isCreditHold}
                    revReq={oe.revReq}
                    onToggleRevReq={(e) => {
                      const next = Boolean(e.target.checked);
                      oe.setRevReq(next);

                      // Excel parity: rev-req allows submit without Sugar note
                      if (next) {
                        setSugarNoteArmed(false);
                      }
                    }}
                    // Hide Sugar link when credit hold is true
                    showInputSugarNote={
                      //! isCreditHold && !revReq && (hasSheets || hasSelectedAddress)  //remove next 2 ines to resutre note validation
                      !isCreditHold &&
                      !oe.revReq &&
                      ((hasSelectedAddress && masId && shipId) || hasSheets)
                    }
                    onInputSugarNote={handleInputSugarNote}
                    showSubmit={
                      hasSheets &&
                      (oe.company === 'tms' || sugarNoteArmed || oe.revReq || isCreditHold)
                    }
                    onSubmit={handleSubmitActiveSheet}
                    sugarButtonRef={sugarButtonRef}
                  />

                  {/* Existing rep identification panel */}
                  <SalesRepsPanel
                    company={oe.company}
                    masId={oe.masId}
                    shipId={oe.shipId}
                    rowHeight={ROW_HEIGHT}
                    salesReps={oe.salesReps}
                    // HOUSE ACCOUNT REP OVERRIDE
                    shipToCode={oe.shipId}
                    houseAccountReps={oe.houseAccountReps}
                    contractNo={oe.contractNo}
                    // NEW — email context
                    shipToName={oe.selectedCustomer || ''}
                    poNumber={oe.poNumber || ''}
                    pickingSheets={pq.sheets}
                    isCreditHold={Boolean(oe.creditHold)} // backend later
                    hasPriceMismatch={Boolean(oe.priceWarningText)}
                    poPrice={oe.price || ''}
                    contractPrice={
                      oe.contractRows?.[0]?.price ? String(oe.contractRows[0].price) : ''
                    }
                  />
                </Stack>
              </Box>
            </Box>
            {/* =========================
              PICKING SHEETS (Floating squares)
              ========================= */}
            <PickingSheetsPanel
              sheets={pq.sheets}
              onRemoveLine={handleRemoveLine}
              onRemoveSheet={handleRemoveSheet}
              onClearAll={handleClearAllSheets}
              onCopyNotes={handleCopyNotesThisSheet}
              submitBusyBySheetId={submitBusyBySheetId}
            />
            {oe.partError && (
              <Typography color="error" variant="body2">
                {oe.partError}
              </Typography>
            )}

            {oe.lookupError && (
              <Typography color="error" variant="body2">
                {oe.lookupError}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
      {/* ====================================================================== */}
      {/* BLOCK OE.FRT_GUARD.DLG.1 — UI: "FC requires valid account" dialog       */}
      {/* ====================================================================== */}
      <Dialog open={frtGuardOpen} onClose={closeFrtGuard} fullWidth maxWidth="xs">
        <DialogTitle>Freight Account Required</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Collect (FC) is selected. Please <b>select PPA</b> or enter a valid <b>UPS</b> /{' '}
            <b>FEDEX</b> freight account number.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFrtGuard}>OK</Button>
          <Button onClick={switchToPpaFromGuard} variant="contained">
            Select PPA
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        ======================================================================
        BLOCK OE.DLG.1 — Save Freight Account dialog (MASID-scoped preview)
        ----------------------------------------------------------------------
        UX goals:
          1) Confirm the user intends to persist a freight account change.
          2) Show EXACTLY what stored rows will be affected before COMMIT.

        Scopes:
          - shipto : update only the current ship-to row
          - all    : insert/update ALL Sage ship-to rows and existing saved rows for this
                    MASID + carrier (includes shiptocode='' only if it already
                    exists in the table).
        ======================================================================
      */}
      <Dialog
        open={oe.freightSaveOpen}
        onClose={handleCloseFreightSaveDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Save Freight Account</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              You entered{' '}
              <b>{String(oe.freightSaveContext?.accountNo || '').trim() || '(blank)'}</b> for
              carrier <b>{oe.freightSaveContext?.carrier}</b>.
            </Typography>

            <FormControl component="fieldset" variant="standard" disabled={oe.freightSaveBusy}>
              <RadioGroup
                value={oe.freightSaveScope}
                onChange={(e) => oe.setFreightSaveScope(e.target.value)}
              >
                <FormControlLabel
                  value="shipto"
                  control={<Radio />}
                  label={`Ship-To only (Sage No. ${oe.freightSaveContext?.customerNo || ''} / Ship-To ${oe.freightSaveContext?.shipToCode || ''})`}
                  disabled={oe.freightSaveBusy || !oe.freightSaveContext?.shipToCode}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={`Apply to all ship-tos for Sage No. ${oe.freightSaveContext?.customerNo || ''} (${oe.freightSaveContext?.carrier || ''})`}
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            <Typography variant="subtitle2">These ship-to accounts will be saved:</Typography>
            <Typography variant="body2">
              Apply to all includes every Sage ship-to for this customer, plus existing saved rows
              and the current ship-to. Missing accounts are created.
            </Typography>
            {oe.freightAccountsLoading && (
              <Alert severity="info">Loading accounts and ship-tos...</Alert>
            )}
            {oe.freightLoadError && (
              <Alert
                severity="error"
                action={
                  <Button onClick={handleRetryFreightAccounts} disabled={oe.freightSaveBusy}>
                    Retry
                  </Button>
                }
              >
                {oe.freightLoadError}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="freight account preview">
                <TableHead>
                  <TableRow>
                    <TableCell>Sage No.</TableCell>
                    <TableCell>Ship-To</TableCell>
                    <TableCell>Carrier</TableCell>
                    <TableCell>Current Account</TableCell>
                    <TableCell>New Account</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(oe.freightSavePreviewRows) ? oe.freightSavePreviewRows : []).map(
                    (r) => (
                      <TableRow key={`${r.customerNo}-${r.shipToCode}-${r.carrier}`}>
                        <TableCell>{r.customerNo}</TableCell>
                        <TableCell>{r.shipToCode === '' ? "'' (default)" : r.shipToCode}</TableCell>
                        <TableCell>{r.carrier}</TableCell>
                        <TableCell>
                          {r.saved ? r.accountNo || '(blank override)' : 'Not saved'}
                        </TableCell>
                        <TableCell>{oe.freightSaveContext?.accountNo}</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {oe.freightSaveError && (
              <Alert severity="error" variant="outlined">
                {oe.freightSaveError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFreightSaveDialog} disabled={oe.freightSaveBusy}>
            CANCEL
          </Button>
          <Button
            onClick={handleSubmitFreightSave}
            disabled={
              oe.freightSaveBusy ||
              !oe.freightAccountsReady ||
              Boolean(oe.freightLoadError) ||
              !oe.freightSavePreviewRows.length
            }
            variant="contained"
          >
            {oe.freightSaveBusy ? 'COMMITTING...' : 'COMMIT'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={oe.freightRemoveOpen}
        onClose={handleCloseFreightRemoveDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remove Freight Account?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              You cleared the Freight Acct field. Do you want to remove saved {oe.carrier} accounts
              for this customer?
            </Typography>

            <FormControl component="fieldset" variant="standard">
              <RadioGroup
                value={oe.freightRemoveScope}
                onChange={(e) => oe.setFreightRemoveScope(e.target.value)}
              >
                <FormControlLabel
                  value="shipto"
                  control={<Radio />}
                  label={`Current Ship-To only (${oe.shipId || 'current ship-to'})`}
                  disabled={!oe.shipId}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="All saved ship-tos for this customer"
                />
              </RadioGroup>
            </FormControl>

            {oe.freightRemoveError && (
              <Alert severity="error" variant="outlined">
                {oe.freightRemoveError}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseFreightRemoveDialog} disabled={oe.freightRemoveBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitFreightRemove}
            disabled={oe.freightRemoveBusy}
            variant="contained"
            color="error"
          >
            {oe.freightRemoveBusy ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <SpecialOrderDialog
        open={oe.specialOpen}
        note={oe.specialNote}
        onClose={handleCloseSpecialDialog}
      />
      {/* ====================================================================== */}
      {/* BLOCK OE.SUBMIT.ERROR_DLG.1 — UI: Submit error dialog                   */}
      {/* ====================================================================== */}
      <Dialog
        open={submitErrorDlg.open}
        onClose={handleCloseSubmitErrorDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{submitErrorDlg.title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity={submitErrorDlg.severity} variant="outlined">
              {submitErrorDlg.message}
            </Alert>

            {submitErrorDlg.details ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {submitErrorDlg.details}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitErrorDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
